import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useClient, useClients, useRateItems } from "@/api/clients";
import { useCreateInvoice, useInvoice, useInvoices } from "@/api/invoices";
import { lateChargeLine, useLateCharge } from "@/api/receivables";
import { useBankAccounts } from "@/api/settings";
import { useVesselCalls } from "@/api/vessels";
import { InvoiceDraftForm } from "@/components/forms/InvoiceDraftForm";
import { RequestError } from "@/components/RequestError";
import { buttonClass } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/page-header";
import { todayIso } from "@/lib/dates";

/** Vessel call ids already on a draft or issued main invoice (cancelled ones free the call;
 *  an additional invoice bills a call again and does not count). */
export function billedCallIds(
  invoices: {
    vessel_call_id: string | null;
    state: string;
    id: string;
    additional_to_id?: string | null;
  }[],
  exceptInvoiceId?: string,
): Set<string> {
  return new Set(
    invoices.flatMap((invoice) =>
      invoice.vessel_call_id &&
      invoice.state !== "cancelled" &&
      !invoice.additional_to_id &&
      invoice.id !== exceptInvoiceId
        ? [invoice.vessel_call_id]
        : [],
    ),
  );
}

type NewDraftProps = {
  clientId: string;
  lateChargeFor: string | null;
  /** An additional invoice: the issued invoice whose vessel call it bills again. */
  additionalTo: string | null;
};

function NewDraft({ clientId, lateChargeFor, additionalTo }: NewDraftProps) {
  const navigate = useNavigate();
  const client = useClient(clientId);
  const calls = useVesselCalls({ clientId, limit: 200 });
  const rateItems = useRateItems(clientId, false);
  const banks = useBankAccounts(false);
  const invoices = useInvoices({ clientId, limit: 200 });
  const create = useCreateInvoice();
  const lateCharge = useLateCharge(lateChargeFor);
  const main = useInvoice(additionalTo ?? "", Boolean(additionalTo));

  const queries = [
    client,
    calls,
    rateItems,
    banks,
    invoices,
    ...(lateChargeFor ? [lateCharge] : []),
    ...(additionalTo ? [main] : []),
  ];
  const loading = queries.some((query) => query.isPending);
  const failed = queries.find((query) => query.isError);
  if (failed) return <RequestError error={failed.error} />;
  if (loading || !client.data || !calls.data || !rateItems.data || !banks.data || !invoices.data) {
    return <p className="text-muted-foreground">Loading the client's calls and rate card…</p>;
  }

  const extraFor = additionalTo ? main.data : undefined;
  if (additionalTo && !extraFor) return <p className="text-muted-foreground">Loading…</p>;
  const billed = billedCallIds(invoices.data);
  // An additional invoice bills the call of the invoice it is additional to, and only that.
  const openCalls = calls.data.filter((call) =>
    extraFor ? call.id === extraFor.vessel_call_id : !billed.has(call.id),
  );
  const autoLines = rateItems.data
    .filter((item) => item.auto_include)
    .map((item) => ({ rate_item_id: item.id, qty: "1" }));
  if (rateItems.data.length === 0) {
    return (
      <EmptyState
        title={`${client.data.code} has no rate card yet`}
        description="Add the fee items from the client's agreement before invoicing."
        action={
          <Link to={`/clients/${clientId}?tab=rates`} className={buttonClass("outline")}>
            Open the rate card
          </Link>
        }
      />
    );
  }
  if (openCalls.length === 0) {
    return (
      <EmptyState
        title={`No unbilled vessel calls for ${client.data.code}`}
        description="Record the vessel call first - arrival, departure and planning dates."
        action={
          <Link to="/vessels" className={buttonClass("outline")}>
            Record a vessel call
          </Link>
        }
      />
    );
  }

  const defaultBank = banks.data.find((bank) => bank.is_default)?.id ?? null;
  return (
    <Card>
      <CardContent>
        {extraFor && (
          <p className="mb-4 text-sm text-muted-foreground">
            An additional invoice for the vessel call already billed on {extraFor.invoice_no}: add
            only the extra work, and its PDF says it is additional to {extraFor.invoice_no}. If
            that work is also on {extraFor.invoice_no}, take it off there with a credit note.
          </p>
        )}
        {lateCharge.data && Number(lateCharge.data.unbilled) > 0 && (
          <p className="mb-4 text-sm text-muted-foreground">
            The late charge on {lateCharge.data.invoice_no} is added as a line of its own. Choose
            the vessel call this invoice is for.
          </p>
        )}
        <InvoiceDraftForm
          calls={openCalls}
          rateItems={rateItems.data}
          bankAccounts={banks.data}
          currency={client.data.currency}
          initial={{
            vessel_call_id: openCalls.length === 1 ? (openCalls[0]?.id ?? "") : "",
            invoice_date: todayIso(),
            payment_days: client.data.payment_days,
            bank_account_id: defaultBank,
            remarks: "",
            lines: [
              ...(extraFor ? [] : autoLines),
              ...(lateCharge.data && Number(lateCharge.data.unbilled) > 0
                ? [lateChargeLine(lateCharge.data)]
                : []),
            ],
          }}
          submitLabel="Save draft"
          onSubmit={async (input) => {
            const invoice = await create.mutateAsync({
              ...input,
              client_id: clientId,
              additional_to_id: extraFor?.id ?? null,
            });
            navigate(`/invoices/${invoice.id}`);
          }}
          onCancel={() => navigate("/invoices")}
        />
      </CardContent>
    </Card>
  );
}

export function NewInvoicePage() {
  const [params] = useSearchParams();
  const clients = useClients("", false);
  const [chosen, setChosen] = useState(params.get("client") ?? "");
  const onlyClient = clients.data?.length === 1 ? clients.data[0]?.id : undefined;
  const clientId = chosen || onlyClient || "";

  return (
    <div className="flex flex-col gap-6">
      <Link to="/invoices" className="text-sm text-muted-foreground hover:text-foreground">
        ← Invoices
      </Link>
      <PageHeader
        title="New invoice"
        description="Saved as a draft first. Preview it, then issue it to give it the next number."
      />
      {clients.isError ? (
        <RequestError error={clients.error} />
      ) : (
        <Card>
          <CardContent>
            <SelectField
              id="new-invoice-client"
              label="Client"
              value={clientId}
              onChange={setChosen}
              options={(clients.data ?? []).map((client) => ({
                value: client.id,
                label: `${client.code} - ${client.legal_name}`,
              }))}
              placeholder={clients.isPending ? "Loading clients…" : "Choose a client"}
            />
          </CardContent>
        </Card>
      )}
      {clientId && (
        <NewDraft
          key={clientId}
          clientId={clientId}
          lateChargeFor={clientId === params.get("client") ? params.get("late_charge") : null}
          additionalTo={clientId === params.get("client") ? params.get("additional_to") : null}
        />
      )}
    </div>
  );
}
